import { Component, h } from "vue";
import { HelpContentType } from "../models/HelpContentType";
import axios from "axios";
import markdownit from 'markdown-it'
import HelpContent from "../components/HelpContent.vue";
import configService from "@/services/configService"
import { getCdnPrefix } from "@/shared/models/DeploymentPrefix"

export abstract class HelpContentFactory<THelpContentType extends HelpContentType | string> {

    //TODO: Get relative deployment path portion from app configuration API (E.g. dev/app/uat/test)
    private readonly _aplosCdnUrl: string = `https://cdn.aplos-nca.com/help/${getCdnPrefix(configService.getApplicationDeploymentPrefix())}/`;
    private readonly _cdnKey: string = "cdn:";


    // Relative path to content for implementing feature area
    abstract cdnContentPath: string;

    abstract tryGetStaticHelpContentByType(type: THelpContentType | string): Promise<Component | null>;

    public isCdnContentType(type: THelpContentType | string): boolean {

        if (type == null) throw Error("type is required");
        return type?.toLowerCase().startsWith(this._cdnKey);

    }

    // Make the cache static
    private static _markdownCache: Map<string, Component> = new Map();

    public async getHelpContentAsync(type: THelpContentType | string): Promise<Component | null> {

        if (type == null) throw Error("type is required");
        if (this.isCdnContentType(type)) return await this.getCdnContentAsync(type);
        return await this.tryGetStaticHelpContentByType(type);
    }

    private async getCdnContentAsync(type: THelpContentType | string): Promise<Component | null> {

        if (type == null) throw Error("type is required");

        try {

            // Check cache first
            if (HelpContentFactory._markdownCache.has(type as string)) {
                return HelpContentFactory._markdownCache.get(type as string)!;
            }

            const contentUri = this.buildCdnContentUri(type);
            const response = await axios.get(contentUri, {
                responseType: 'text',
                withCredentials: false,
                headers: {
                    'Accept': 'text/markdown, text/plain, */*'
                }
            });


            const markdownText = response.data;
            const md = markdownit({ html: true })
            const htmlContent = md.render(markdownText);
            const dynamicMarkdownComponent = {
                render() {
                    return h(HelpContent, { htmlContent: htmlContent });
                }
            };

            // Cache the result
            HelpContentFactory._markdownCache.set(type as string, dynamicMarkdownComponent);

            return dynamicMarkdownComponent;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                console.error('Axios error message:', error.message);
            }
            else console.log(error);
            return null;
        }

    }

    public buildCdnContentUri(type: THelpContentType | string): string {

        if (type == null) throw Error("type is required");
        if (!this.isCdnContentType(type)) throw Error("valid cdn content type required");

        const contentPath = type.slice(this._cdnKey.length);
        return `${this._aplosCdnUrl}${this.cdnContentPath}/${contentPath}`;

    }

}
